import { useMemo } from "react";
import type { SurfaceKey } from "types/interfaces/access";
import { can, canInClientView, openAction } from "utils/access";
import { useViewAs } from "./useViewAs";

export interface Permissions {
  /** Whether the page shows this: the viewed user inside the Client View shell. */
  sees: (key: string) => boolean;
  seesSurface: (surface: SurfaceKey) => boolean;
  /** Whether the logged-in user may do this right now (never in Client View). */
  mayDo: (key: string) => boolean;
  /** Whether the logged-in user holds this, Client View or not. */
  holds: (key: string) => boolean;
}

export function usePermissions(): Permissions {
  const { isClientView, readOnly, profile, realProfile, basePath } =
    useViewAs();
  // The slice outlives the shell by a render after a browser Back, so only a
  // page inside the shell is seen through the viewed user.
  const inShell = isClientView && basePath !== "";
  return useMemo(() => {
    const sees = (key: string) =>
      inShell
        ? canInClientView(profile, realProfile, key)
        : can(realProfile, key);
    return {
      sees,
      seesSurface: (surface: SurfaceKey) => sees(openAction(surface)),
      mayDo: (key: string) => !readOnly && can(realProfile, key),
      holds: (key: string) => can(realProfile, key),
    };
  }, [inShell, readOnly, profile, realProfile]);
}

export default usePermissions;
